import React, { useState, useRef } from 'react';

import {
  useWebformElement,
  WebformElementWrapper,
  WebformCustomComponent,
} from 'gatsby-drupal-webform';

export const DEFAULT_SELECT_LABEL = '-- Select --';

/* eslint-disable react/prop-types */

export const WebformSelect: WebformCustomComponent = ({ element, error }) => {
  const elementRef = useRef();
  const selectFieldRef = useRef<HTMLDivElement>(null);
  const [selectedValue, setSelectedValue] = useState('');
  const [selectedValues, setSelectedValues] = useState([] as string[]);
  const [open, setOpen] = useState(false);
  const id = `form-${element.name}`.replace(/[_\s]/g, '-');
  const [inputProps, settings] = useWebformElement(element, {
    className: 'form-control hide-field',
    name: element.name,
    id,
  });

  // Detect if field is multiselect from Drupal element or merged props
  const isMultiple = Boolean(
    (element && ((element as any).multiple || (element as any).multiselect)) ||
    ((element as any)?.properties && (((element as any).properties as any).multiple || ((element as any).properties as any).multiselect)) ||
    (inputProps as any)?.multiple
  );

  // Initialize state from defaultValue
  React.useEffect(() => {
    const dv: any = (inputProps as any)?.defaultValue;
    if (isMultiple) {
      if (Array.isArray(dv)) {
        setSelectedValues(dv.map(function (x) { return String(x); }));
      } else if (dv != null && dv !== '') {
        setSelectedValues([String(dv)]);
      } else {
        setSelectedValues([]);
      }
    } else {
      setSelectedValue(typeof dv === 'string' ? dv : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiple]);

  // Close dropdown on outside click
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectFieldRef.current && !selectFieldRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const onSelect = (label: string, value: string | null) => {
    if (isMultiple) {
      // Toggle value in selection list or clear when null
      if (value === null) {
        setSelectedValues([]);
      } else {
        const v = String(value);
        setSelectedValues(function (prev: string[]) {
          const exists = (prev as string[]).indexOf(v) !== -1;
          if (exists) {
            return (prev as string[]).filter(function (it) { return it !== v; });
          }
          return (prev as string[]).concat([v]);
        });
      }
    } else {
      setOpen(false);
      setSelectedValue(label);
      if (elementRef.current) {
        (elementRef.current as any).value = value;
      }
    }
  };

  // Helper to compute selected labels for multiselect display
  function getSelectedLabels(): string[] {
    if (!isMultiple || !(element as any).options) return [];
    var labels: string[] = [];
    var values = selectedValues;
    for (var i = 0; i < values.length; i++) {
      var found = null as any;
      for (var j = 0; j < (element as any).options.length; j++) {
        var opt = (element as any).options[j];
        if (String(opt.value) === String(values[i])) { found = opt; break; }
      }
      labels.push(found ? String(found.label) : String(values[i]));
    }
    return labels;
  }

  return (
    <WebformElementWrapper settings={settings} error={error} labelFor={id}>
      <select
        {...inputProps}
        multiple={isMultiple || undefined}
        {...(isMultiple
          ? { value: selectedValues }
          : { defaultValue: (inputProps as any).defaultValue || '' })}
        onChange={function (e: React.ChangeEvent<HTMLSelectElement>) {
          if (isMultiple) {
            var opts: string[] = [];
            var sel = e.target && (e.target as any).selectedOptions;
            if (sel && sel.length) {
              for (var i = 0; i < sel.length; i++) {
                var o = sel.item(i) as any;
                if (o) opts.push(String(o.value));
              }
            }
            setSelectedValues(opts);
          } else {
            var val = (e.target as any).value;
            var label = val;
            if ((element as any).options) {
              for (var k = 0; k < (element as any).options.length; k++) {
                var op = (element as any).options[k];
                if (String(op.value) === String(val)) { label = String(op.label); break; }
              }
            }
            setSelectedValue(String(label));
          }
        }}
        ref={elementRef}
      >
        {/** Render placeholder as first element (single only) */}
        {!isMultiple && (
          <option value="" disabled>
            {(inputProps as any).placeholder || DEFAULT_SELECT_LABEL}
          </option>
        )}

        {element.options &&
          element.options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
      </select>
      <div className={`select-field ${open ? 'open' : ''}`} ref={selectFieldRef}>
        <div
          className="select-button"
          onClick={() => setOpen(!open)}
        >
          {isMultiple ? (
            <div className="select-chips-container">
              {(function () {
                var labels = getSelectedLabels();
                if (labels.length === 0) {
                  return <span className="select-placeholder">{(inputProps as any).placeholder || DEFAULT_SELECT_LABEL}</span>;
                }
                return labels.map(function (label, idx) {
                  return (
                    <span key={idx} className="select-chip">
                      <span className="select-chip-label">{label}</span>
                      <button
                        type="button"
                        className="select-chip-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          var valueToRemove = selectedValues[idx];
                          setSelectedValues(function (prev) {
                            return prev.filter(function (v) { return v !== valueToRemove; });
                          });
                        }}
                        aria-label={`Remove ${label}`}
                      >
                        ×
                      </button>
                    </span>
                  );
                });
              })()}
            </div>
          ) : (
            <span>{selectedValue !== '' ? selectedValue : (inputProps as any).placeholder || DEFAULT_SELECT_LABEL}</span>
          )}
        </div>
        {element.options && open && (
          <div className="select-options">
            {!isMultiple && (
              <button
                key={(inputProps as any).placeholder || DEFAULT_SELECT_LABEL}
                type="button"
                onClick={() =>
                  onSelect((inputProps as any).placeholder || DEFAULT_SELECT_LABEL, null)
                }
              >
                {(inputProps as any).placeholder || DEFAULT_SELECT_LABEL}
              </button>
            )}
            {isMultiple && (
              <button
                key="__clear__"
                type="button"
                onClick={() => onSelect('' as any, null)}
              >
                Clear selection
              </button>
            )}
            {element.options.map(option => (
              <button
                className={
                  isMultiple
                    ? (selectedValues.indexOf(String(option.value)) !== -1 ? 'selected' : '')
                    : (selectedValue === option.label ? 'selected' : '')
                }
                key={option.value}
                type="button"
                onClick={() => onSelect(option.label, option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </WebformElementWrapper>
  );
};

export default WebformSelect;

